import { create } from 'zustand';
import { RentDiscount, DiscountType, DiscountStatus } from '../types';
import { supabase } from '../api/supabase';

// ===== TYPE DEFINITIONS =====

export interface RentDiscountFilters {
  driverId?: string;
  vehicleId?: string;
  settlementId?: string;
  status?: DiscountStatus | DiscountStatus[];
  month?: number;
  year?: number;
  discountType?: DiscountType;
  startDate?: string;
  endDate?: string;
}

export interface DiscountRequestData {
  driver_id: string;
  vehicle_id: string;
  discount_type: DiscountType;
  discount_date: string;  // ISO date
  discount_days: number;
  daily_rate: number;
  reason: string;
  notes?: string;
  requested_by?: string;  // User ID who requested
}

export interface DiscountApprovalResult {
  success: boolean;
  discount: RentDiscount;
  message?: string;
}

export interface MonthlyDiscountSummary {
  driver_id: string;
  driver_name: string;
  month: number;
  year: number;
  total_discount_days: number;
  total_discount_amount: number;
  pending_days: number;
  approved_days: number;
  rejected_days: number;
  discounts: RentDiscount[];
}

// System settings from database
const MAX_DISCOUNT_DAYS_PER_MONTH = 3;

// ===== STORE INTERFACE =====

interface RentDiscountStore {
  discounts: RentDiscount[];
  selectedDiscount: RentDiscount | null;
  pendingDiscounts: RentDiscount[];
  monthlyDiscountSummary: MonthlyDiscountSummary | null;
  loading: boolean;
  error: string | null;

  // CRUD operations
  fetchDiscounts: (filters?: RentDiscountFilters) => Promise<void>;
  fetchDiscountById: (id: string) => Promise<void>;
  requestDiscount: (requestData: DiscountRequestData) => Promise<string>;
  approveDiscount: (discountId: string, approverId: string) => Promise<DiscountApprovalResult>;
  rejectDiscount: (discountId: string, reason: string, rejectedBy: string) => Promise<void>;
  updateDiscountNotes: (discountId: string, notes: string) => Promise<void>;
  deleteDiscount: (discountId: string) => Promise<void>;

  // Query functions
  getPendingDiscounts: () => Promise<void>;
  getDiscountsByDriver: (driverId: string, month?: number, year?: number) => Promise<void>;
  getDiscountsBySettlement: (settlementId: string) => Promise<void>;
  getMonthlyDiscountSummary: (driverId: string, month: number, year: number) => Promise<MonthlyDiscountSummary>;
  validateDiscountRequest: (driverId: string, month: number, year: number, newDays: number) => Promise<{ valid: boolean; message: string; currentDays: number }>;

  // Helper functions
  setSelectedDiscount: (discount: RentDiscount | null) => void;
  clearError: () => void;
}

// ===== STORE IMPLEMENTATION =====

export const useRentDiscountStore = create<RentDiscountStore>((set, get) => ({
  discounts: [],
  selectedDiscount: null,
  pendingDiscounts: [],
  monthlyDiscountSummary: null,
  loading: false,
  error: null,

  fetchDiscounts: async (filters?: RentDiscountFilters) => {
    set({ loading: true, error: null });
    try {
      let query = supabase
        .from('rent_discounts')
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone),
          vehicle:vehicles!vehicle_id(id, license_plate, make_model)
        `)
        .order('discount_date', { ascending: false })
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.driverId) {
        query = query.eq('driver_id', filters.driverId);
      }
      if (filters?.vehicleId) {
        query = query.eq('vehicle_id', filters.vehicleId);
      }
      if (filters?.settlementId) {
        query = query.eq('settlement_id', filters.settlementId);
      }
      if (filters?.status) {
        if (Array.isArray(filters.status)) {
          query = query.in('status', filters.status);
        } else {
          query = query.eq('status', filters.status);
        }
      }
      if (filters?.discountType) {
        query = query.eq('discount_type', filters.discountType);
      }
      if (filters?.startDate) {
        query = query.gte('discount_date', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('discount_date', filters.endDate);
      }

      // Month/Year filter (filter by discount_date)
      if (filters?.year && filters?.month) {
        const startDate = new Date(filters.year, filters.month - 1, 1).toISOString().split('T')[0];
        const endDate = new Date(filters.year, filters.month, 0).toISOString().split('T')[0];
        query = query.gte('discount_date', startDate).lte('discount_date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ discounts: data || [], loading: false });
    } catch (error: any) {
      console.error('Error fetching rent discounts:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchDiscountById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('rent_discounts')
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone),
          vehicle:vehicles!vehicle_id(id, license_plate, make_model)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      set({ selectedDiscount: data, loading: false });
    } catch (error: any) {
      console.error('Error fetching discount:', error);
      set({ error: error.message, loading: false });
    }
  },

  requestDiscount: async (requestData: DiscountRequestData) => {
    set({ loading: true, error: null });
    try {
      // Calculate total discount amount
      const totalAmount = requestData.discount_days * requestData.daily_rate;

      // Validate discount days for the month
      const discountDate = new Date(requestData.discount_date);
      const month = discountDate.getMonth() + 1;
      const year = discountDate.getFullYear();

      const validation = await get().validateDiscountRequest(
        requestData.driver_id,
        month,
        year,
        requestData.discount_days
      );

      if (!validation.valid) {
        throw new Error(validation.message);
      }

      // Insert discount request
      const { data, error } = await supabase
        .from('rent_discounts')
        .insert([
          {
            driver_id: requestData.driver_id,
            vehicle_id: requestData.vehicle_id,
            discount_type: requestData.discount_type,
            discount_date: requestData.discount_date,
            discount_days: requestData.discount_days,
            daily_rate: requestData.daily_rate,
            total_discount_amount: totalAmount,
            reason: requestData.reason,
            notes: requestData.notes,
            status: 'pending',
            requested_by: requestData.requested_by,
            requested_at: new Date().toISOString(),
          },
        ])
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone),
          vehicle:vehicles!vehicle_id(id, license_plate, make_model)
        `)
        .single();

      if (error) throw error;

      // Update local state
      set((state) => ({
        discounts: [data, ...state.discounts],
        pendingDiscounts: [data, ...state.pendingDiscounts],
        loading: false,
      }));

      return data.id;
    } catch (error: any) {
      console.error('Error requesting discount:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  approveDiscount: async (discountId: string, approverId: string) => {
    set({ loading: true, error: null });
    try {
      // Fetch the discount first
      const { data: discount, error: fetchError } = await supabase
        .from('rent_discounts')
        .select('*')
        .eq('id', discountId)
        .single();

      if (fetchError) throw fetchError;

      if (discount.status !== 'pending') {
        throw new Error('Discount is not in pending status');
      }

      // Validate discount days for the month
      const discountDate = new Date(discount.discount_date);
      const month = discountDate.getMonth() + 1;
      const year = discountDate.getFullYear();

      const validation = await get().validateDiscountRequest(
        discount.driver_id,
        month,
        year,
        discount.discount_days
      );

      // Allow approval even if exceeds limit (manager override)
      // but warn in the result
      let message = validation.valid
        ? 'Discount approved successfully'
        : `Warning: Approved ${validation.currentDays + discount.discount_days} days for ${year}-${month} (exceeds ${MAX_DISCOUNT_DAYS_PER_MONTH} day limit)`;

      // Update discount to approved
      const { data, error } = await supabase
        .from('rent_discounts')
        .update({
          status: 'approved',
          approved_by: approverId,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', discountId)
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone),
          vehicle:vehicles!vehicle_id(id, license_plate, make_model)
        `)
        .single();

      if (error) throw error;

      // Update local state
      set((state) => ({
        discounts: state.discounts.map((d) => (d.id === discountId ? data : d)),
        pendingDiscounts: state.pendingDiscounts.filter((d) => d.id !== discountId),
        selectedDiscount: state.selectedDiscount?.id === discountId ? data : state.selectedDiscount,
        loading: false,
      }));

      return {
        success: true,
        discount: data,
        message,
      };
    } catch (error: any) {
      console.error('Error approving discount:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  rejectDiscount: async (discountId: string, reason: string, rejectedBy: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('rent_discounts')
        .update({
          status: 'rejected',
          rejection_reason: reason,
          approved_by: rejectedBy,  // Track who rejected
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', discountId)
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone),
          vehicle:vehicles!vehicle_id(id, license_plate, make_model)
        `)
        .single();

      if (error) throw error;

      // Update local state
      set((state) => ({
        discounts: state.discounts.map((d) => (d.id === discountId ? data : d)),
        pendingDiscounts: state.pendingDiscounts.filter((d) => d.id !== discountId),
        selectedDiscount: state.selectedDiscount?.id === discountId ? data : state.selectedDiscount,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error rejecting discount:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateDiscountNotes: async (discountId: string, notes: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('rent_discounts')
        .update({
          notes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', discountId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        discounts: state.discounts.map((d) => (d.id === discountId ? { ...d, notes } : d)),
        selectedDiscount:
          state.selectedDiscount?.id === discountId ? { ...state.selectedDiscount, notes } : state.selectedDiscount,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error updating notes:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteDiscount: async (discountId: string) => {
    set({ loading: true, error: null });
    try {
      // Check if discount is used in a settlement
      const { data: discount } = await supabase
        .from('rent_discounts')
        .select('settlement_id, status')
        .eq('id', discountId)
        .single();

      if (discount?.settlement_id) {
        throw new Error('Cannot delete discount that is linked to a settlement');
      }

      if (discount?.status === 'approved') {
        throw new Error('Cannot delete approved discount. Reject it instead.');
      }

      const { error } = await supabase.from('rent_discounts').delete().eq('id', discountId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        discounts: state.discounts.filter((d) => d.id !== discountId),
        pendingDiscounts: state.pendingDiscounts.filter((d) => d.id !== discountId),
        selectedDiscount: state.selectedDiscount?.id === discountId ? null : state.selectedDiscount,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error deleting discount:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  getPendingDiscounts: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('rent_discounts')
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone),
          vehicle:vehicles!vehicle_id(id, license_plate, make_model)
        `)
        .eq('status', 'pending')
        .order('requested_at', { ascending: true });  // Oldest first

      if (error) throw error;

      set({ pendingDiscounts: data || [], loading: false });
    } catch (error: any) {
      console.error('Error fetching pending discounts:', error);
      set({ error: error.message, loading: false });
    }
  },

  getDiscountsByDriver: async (driverId: string, month?: number, year?: number) => {
    await get().fetchDiscounts({
      driverId,
      month,
      year,
    });
  },

  getDiscountsBySettlement: async (settlementId: string) => {
    await get().fetchDiscounts({
      settlementId,
    });
  },

  getMonthlyDiscountSummary: async (driverId: string, month: number, year: number) => {
    try {
      // Fetch all discounts for driver in the month
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];

      const { data: discounts, error } = await supabase
        .from('rent_discounts')
        .select(`
          *,
          driver:drivers!driver_id(id, name, phone)
        `)
        .eq('driver_id', driverId)
        .gte('discount_date', startDate)
        .lte('discount_date', endDate)
        .order('discount_date', { ascending: false });

      if (error) throw error;

      // Calculate summary
      let totalDays = 0;
      let totalAmount = 0;
      let pendingDays = 0;
      let approvedDays = 0;
      let rejectedDays = 0;

      (discounts || []).forEach((discount) => {
        if (discount.status === 'pending') {
          pendingDays += discount.discount_days;
        } else if (discount.status === 'approved') {
          approvedDays += discount.discount_days;
          totalDays += discount.discount_days;
          totalAmount += discount.total_discount_amount;
        } else if (discount.status === 'rejected') {
          rejectedDays += discount.discount_days;
        }
      });

      const driver = discounts?.[0]?.driver;
      const driverData = Array.isArray(driver) ? driver[0] : driver;

      const summary: MonthlyDiscountSummary = {
        driver_id: driverId,
        driver_name: driverData?.name || 'Unknown',
        month,
        year,
        total_discount_days: totalDays,
        total_discount_amount: totalAmount,
        pending_days: pendingDays,
        approved_days: approvedDays,
        rejected_days: rejectedDays,
        discounts: discounts || [],
      };

      set({ monthlyDiscountSummary: summary });

      return summary;
    } catch (error: any) {
      console.error('Error fetching monthly discount summary:', error);
      throw error;
    }
  },

  validateDiscountRequest: async (driverId: string, month: number, year: number, newDays: number) => {
    try {
      // Fetch approved discounts for the month
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];

      const { data: approvedDiscounts, error } = await supabase
        .from('rent_discounts')
        .select('discount_days')
        .eq('driver_id', driverId)
        .eq('status', 'approved')
        .gte('discount_date', startDate)
        .lte('discount_date', endDate);

      if (error) throw error;

      // Calculate current approved days
      const currentDays = (approvedDiscounts || []).reduce((sum, d) => sum + d.discount_days, 0);
      const totalDays = currentDays + newDays;

      if (totalDays > MAX_DISCOUNT_DAYS_PER_MONTH) {
        return {
          valid: false,
          message: `Exceeds maximum ${MAX_DISCOUNT_DAYS_PER_MONTH} discount days per month. Current: ${currentDays} days, Requested: ${newDays} days, Total: ${totalDays} days. Manager approval required.`,
          currentDays,
        };
      }

      return {
        valid: true,
        message: `Valid: ${currentDays} current days + ${newDays} new days = ${totalDays} days (within ${MAX_DISCOUNT_DAYS_PER_MONTH} day limit)`,
        currentDays,
      };
    } catch (error: any) {
      console.error('Error validating discount request:', error);
      return {
        valid: false,
        message: `Validation error: ${error.message}`,
        currentDays: 0,
      };
    }
  },

  setSelectedDiscount: (discount: RentDiscount | null) => {
    set({ selectedDiscount: discount });
  },

  clearError: () => {
    set({ error: null });
  },
}));
